"use client";

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import { flushSync } from "react-dom";
import type { Role } from "@elevenlabs/react";
import { getLocalStorage, LocalStorageKeys, setLocalStorage } from "@utilities/localStorage";
import { OrbDisc } from "@ui/components/core/Orb/OrbDisc";
import { OrbWithMessages } from "@ui/components/core/Orb/OrbWithMessages";
import { TextMessageList } from "@ui/components/core/Orb/TextMessageList";
import { InitialMessages } from "@ui/components/core/Orb/InitialMessages";
import type { OrbMessage } from "@ui/components/core/Orb/types";
import { useAgentSession } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/useAgentSession";
import { AgentCallButton } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/AgentCallButton";
import { AgentTextInput } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/AgentTextInput";
import { AgentTermsOverlay } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/AgentTermsOverlay";
import type { AgentOrbTextureName } from "@ui/components/core/Orb/textures";
import { CallAgentPillButton, GetCallPillButton, EndCallPillButton } from "./views";
import { useConvAILanguage } from "@ui/hooks/useConvAILanguage";

export type AgentsHeroDemoHandle = {
  endSession: () => void;
};

export type AgentsHeroDemoProps = {
  agentId: string;
  voiceId?: string | null;
  mode: "text" | "voice";
  initialMessages?: { role: Role; message: string }[];
  className?: string;
  onSessionActiveChange?: (active: boolean) => void;
  onModeChange?: (mode: "text" | "voice") => void;
  callAgentButtonText?: string | null;
  getCallButtonText?: string | null;
  /** Called when the user clicks "Get Call" in voice mode. Parent should show the GetCallFlow. */
  onGetCallRequested?: () => void;
  /** Where chat messages should anchor in chat mode. Defaults to "top". */
  chatAnchor?: "top" | "bottom";
  orbTexture: AgentOrbTextureName;
};

export const AgentsHeroDemo = forwardRef<AgentsHeroDemoHandle, AgentsHeroDemoProps>(function AgentsHeroDemo(
  {
    agentId,
    voiceId,
    mode: controlledMode,
    initialMessages,
    className,
    onSessionActiveChange,
    onModeChange,
    callAgentButtonText,
    getCallButtonText,
    onGetCallRequested,
    chatAnchor = "top",
    orbTexture,
  }: AgentsHeroDemoProps,
  ref,
) {
  const [showTermsFor, setShowTermsFor] = useState<"text" | "voice" | null>(null);
  const pendingTextRef = useRef("");
  const { language } = useConvAILanguage();

  const initialMessagesForDisplay: OrbMessage[] = useMemo(
    () => (initialMessages ?? []).map((msg, i) => ({ id: -(i + 1), ...msg })),
    [initialMessages],
  );

  const session = useAgentSession({
    agentId,
    voiceId,
    defaultMode: controlledMode,
    onDisconnect: () => setShowTermsFor(null),
  });

  const showInitialMessages = !session.sessionIsActive && initialMessagesForDisplay.length > 0;

  useEffect(() => {
    onSessionActiveChange?.(session.sessionIsActive);
  }, [session.sessionIsActive, onSessionActiveChange]);

  useEffect(() => {
    if (controlledMode !== session.mode) {
      session.setMode(controlledMode);
      setShowTermsFor(null);
    }
  }, [controlledMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose imperative API to parent for ending the session on demand.
  useImperativeHandle(
    ref,
    () => ({
      endSession: () => {
        if (session.sessionIsActive) session.endSession();
      },
    }),
    [session],
  );

  const isVoiceMode = controlledMode === "voice";
  const showPillButtons = isVoiceMode && onGetCallRequested;

  const startVoiceSession = () => {
    if (session.sessionIsActive || session.status !== "disconnected") return;
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      onModeChange?.("voice");
      session.startVoiceSession({ language: language.value });
    } else {
      setShowTermsFor("voice");
    }
  };

  const fadeProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  };

  return (
    <div
      className={clsx(
        "tw-relative tw-w-[22.5rem] tw-max-w-full tw-flex tw-flex-col tw-px-2 tw-py-4 tw-h-[500px]",
        showTermsFor ? "tw-overflow-y-auto tw-scrollbar-none" : "tw-overflow-hidden",
        className,
      )}
    >
      <m.div
        className='tw-absolute tw-inset-0 tw-bg-white tw-shadow-lg tw-rounded-[2rem] tw-pointer-events-none'
        initial={false}
        animate={{ opacity: isVoiceMode ? 0 : 1 }}
        transition={{ duration: 0.3 }}
      />
      <div className='sm:tw-relative tw-flex-1 tw-flex tw-min-h-0 tw-flex-col tw-items-center'>
        <m.div
          initial={false}
          animate={showTermsFor === null ? { opacity: 1, transition: { delay: 0.15 } } : { opacity: 0 }}
          inert={showTermsFor !== null}
          className={clsx(
            "tw-max-w-full tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-items-center",
            isVoiceMode && "tw-justify-center",
          )}
        >
          {isVoiceMode ? (
            <div className='tw-px-4 tw-relative'>
              <OrbWithMessages
                mode={session.mode}
                texture={orbTexture}
                conversation={session.conversation}
                messages={session.messages}
                alignMessages='top'
                agentMessageStyle='warm'
                userMessageStyle='elevated'
              />
            </div>
          ) : (
            <div className='tw-relative tw-isolate tw-w-80 tw-max-w-full tw-flex-1 tw-min-h-0 tw-flex tw-flex-col'>
              <div className='tw-relative tw-w-full tw-h-12 tw-flex-shrink-0'>
                <OrbDisc mode={session.mode} texture={orbTexture} conversation={session.conversation} />
              </div>
              <div className='tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-pt-4'>
                {showInitialMessages ? (
                  <div className={clsx(chatAnchor === "bottom" ? "tw-mt-auto" : undefined)}>
                    <InitialMessages
                      messages={initialMessagesForDisplay}
                      agentMessageStyle='warm'
                      gap='compact'
                      pointyCorners
                    />
                  </div>
                ) : (
                  <TextMessageList
                    messages={session.messages}
                    fillHeight
                    agentMessageStyle='warm'
                    alignMessages={chatAnchor}
                    userMessageStyle='elevated'
                    gap='compact'
                    pointyCorners
                  />
                )}
              </div>
            </div>
          )}
          {showPillButtons ? (
            <div className='tw-mt-8'>
              <AnimatePresence mode='wait' initial={false}>
                {session.sessionIsActive ? (
                  <m.div key='end-call' {...fadeProps}>
                    <EndCallPillButton onClick={() => session.endSession()} />
                  </m.div>
                ) : (
                  <m.div
                    key='action-buttons'
                    {...fadeProps}
                    className='tw-flex tw-items-center tw-justify-center tw-gap-10'
                  >
                    <CallAgentPillButton onClick={startVoiceSession} label={callAgentButtonText} />
                    <GetCallPillButton onClick={onGetCallRequested} label={getCallButtonText} />
                  </m.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div
              className={clsx(
                "tw-relative tw-flex tw-w-full",
                !isVoiceMode ? "tw-mt-auto tw-pt-4" : "tw-mt-8",
                isVoiceMode && "tw-justify-center",
              )}
            >
              {!isVoiceMode && (
                <div className={clsx("tw-w-0", session.voiceIsActive ? "tw-flex-auto tw-mr-2.5" : "tw-flex-none")} />
              )}
              <AgentCallButton
                sessionIsActive={session.sessionIsActive}
                isTextMode={!isVoiceMode}
                onToggle={() => {
                  if (session.sessionIsActive) {
                    session.endSession();
                  } else if (session.status === "disconnected") {
                    startVoiceSession();
                  }
                }}
                className={clsx(!isVoiceMode && "tw-ml-auto")}
              />
              {!isVoiceMode && (
                <AgentTextInput
                  voiceIsActive={session.voiceIsActive}
                  enableInput={session.sessionIsActive}
                  sessionIsActive={session.sessionIsActive}
                  isPendingDisconnect={session.isPendingDisconnect}
                  onSubmit={text => {
                    if (session.sessionIsActive) {
                      session.sendTextMessage(text);
                    } else {
                      const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
                      if (termsAccepted) {
                        session.startTextSessionWithMessage(text, { language: language.value });
                      } else {
                        pendingTextRef.current = text;
                        setShowTermsFor("text");
                      }
                    }
                  }}
                  onActivate={() => {
                    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
                    if (termsAccepted) {
                      session.startTextSession({ language: language.value });
                    } else {
                      setShowTermsFor("text");
                    }
                  }}
                  textInputRef={session.textInputRef}
                />
              )}
            </div>
          )}
        </m.div>
        <div className='tw-absolute tw-inset-2 tw-z-20 tw-pointer-events-none'>
          <AgentTermsOverlay
            showTermsFor={showTermsFor}
            showGradients={false}
            scrollOnCard
            onAgree={() => {
              setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
              const target = showTermsFor;
              flushSync(() => {
                setShowTermsFor(null);
              });
              if (target === "text") {
                const text = pendingTextRef.current;
                pendingTextRef.current = "";
                if (text) {
                  session.startTextSessionWithMessage(text, { language: language.value });
                } else {
                  session.startTextSession({ language: language.value });
                }
              } else {
                onModeChange?.("voice");
                session.startVoiceSession({ language: language.value });
              }
            }}
            onClose={() => setShowTermsFor(null)}
          />
        </div>
      </div>
    </div>
  );
});
